import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * `elevated` is the shipped look: a shadow, no border. `flat` swaps it for a
 * sunken fill and a hairline, which is what the console's
 * `.table-settings-header` cards use — a strip that sits *on* a surface
 * rather than floating above it.
 */
export type BzCardAppearance = 'elevated' | 'flat';

/**
 * A surface: the thing a screen's content sits on.
 *
 * Not built on `mat-card`. A card is a rounded box with a shadow and three
 * padded regions — there is no behaviour, no focus management and no overlay
 * for Material to own, so importing it would buy a second opinion about
 * padding and typography and nothing else.
 *
 * Content arrives through slots rather than `title`/`subtitle` inputs, which
 * is a deliberate departure from `bz-dialog-layout`. The console's 72
 * `md-card-header`s are not one shape: some are a title, some a title over a
 * subtitle, some a title with a trailing help icon and tooltip, some a
 * clickable row that expands the card, and several a hand-rolled two-column
 * layout. A `heading` input would have served the first and fought the rest.
 *
 * ```html
 * <bz-card>
 *   <div bzCardHeader>Routing settings</div>
 *   <p>…</p>
 *   <div bzCardActions><bz-button>Save</bz-button></div>
 * </bz-card>
 * ```
 *
 * Both slots are optional and collapse when nothing is projected into them,
 * so a plain content card needs no ceremony.
 */
@Component({
  selector: 'bz-card',
  templateUrl: './bz-card.html',
  styleUrl: './bz-card.scss',
  host: {
    '[class]': 'hostClass()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzCard {
  readonly appearance = input<BzCardAppearance>('elevated');

  /**
   * Padding around the body. Turn it off for content that has to run to the
   * card's own edges — a table, a full-width list. The console does this by
   * hand 36 times (`md-card-content class="padding-0"`), which is what makes
   * it an input rather than something a caller overrides in CSS.
   *
   * Only the body is affected: a header and an actions row still need their
   * padding, or they would sit flush against the card's corners.
   */
  readonly padded = input(true);

  protected readonly hostClass = computed(
    () =>
      `bz-card bz-card--${this.appearance()}` + (this.padded() ? '' : ' bz-card--flush'),
  );
}
